import type { DRDrawing, DRLocalDrawingMeta } from "@/types";

export function getLastEditedDate(
  drawing?: DRLocalDrawingMeta | DRDrawing | null
) {
  if (drawing?.lastRevision.editedAt) {
    return new Date(drawing.lastRevision.editedAt);
  } else if (drawing?.lastRevision.savedAt) {
    return new Date(drawing.lastRevision.savedAt);
  }
  return undefined;
}

/**
 * Prend un `DRLocalDrawingMeta` et NON un `DRLocalDrawingData` : c'est un test de
 * présence de brouillon, il ne doit jamais forcer le chargement des binaires (cf.
 * `DRRevisionMeta` et `localDrawingRepository.fetchAllMetaByNewest`).
 */
export function isDrawingSynced(drawing?: DRLocalDrawingMeta) {
  if (!drawing) return false;
  // A drawing is synced when there's no unsaved draft (VSFX or DWG)
  return !drawing.lastRevision.hasDraft && !drawing.lastRevision.hasDwgDraft;
}

/**
 * Dessin dont la révision locale est à l'ancien format ODA.
 *
 * Il ne peut PAS être re-téléchargé : `Syncer.makeVsfxLocal` lève une erreur sans
 * condition depuis la fin du support ODA, et l'UI n'offre alors qu'un badge « Format
 * legacy » non cliquable (cf. `DrawingInfo.vue`). La copie locale est donc la seule
 * encore accessible à l'utilisateur, ce qui rend toute éviction irréversible.
 *
 * Se lit sur `format`, champ non binaire conservé par `DRRevisionMeta` — et le même
 * signal que celui utilisé par `makeDrawingFileLocal` pour router le téléchargement.
 * Les dessins créés dans l'app ne sont pas concernés : leur ligne locale temporaire
 * (format VSFX, id négatif) est remplacée après création serveur par une révision
 * DWG (cf. `Syncer.createNewDrawing`).
 */
export function isLegacyFormatDrawing(drawing: DRLocalDrawingMeta): boolean {
  const { format } = drawing.lastRevision;
  return format === "VSFX" || format === "VSF";
}

/**
 * Date de référence de la politique de rétention locale : la plus récente entre la
 * dernière édition et la mise à disposition hors ligne (`localizedAt`).
 *
 * Les deux comptent : `editedAt` peut dater de six mois sur un dessin téléchargé ce
 * matin pour une visite terrain (`saveLastRevisionDwg` écrase la révision locale avec
 * les données SERVEUR, `editedAt` inclus) — le purger au prochain lancement serait le
 * contraire de ce que l'utilisateur vient de demander.
 *
 * Sur les lignes antérieures à l'introduction du champ, `localizedAt` est absent et on
 * retombe sur la seule date d'édition. CHOIX DÉLIBÉRÉ, pas un oubli : au premier
 * lancement suivant le déploiement, tout le parc local inactif depuis plus de deux mois
 * est donc évincé d'un coup. C'est l'essentiel du gain attendu — `DrawingItem.open()`
 * rend local tout dessin ouvert, si bien que ce parc accumule sans limite tout ce que
 * l'utilisateur a consulté un jour. Contrepartie assumée : une visite terrain préparée
 * juste avant le déploiement, sur des dessins anciens, part avec le lot (rien n'est
 * perdu, mais il faut être en ligne pour les reprendre). Un backfill IDB corrigerait ce
 * cas au prix d'une réécriture de chaque ligne, binaires compris — des centaines de Mo
 * d'I/O au démarrage.
 */
export function getLocalRetentionDate(
  drawing: DRLocalDrawingMeta
): Date | undefined {
  const editedAt = getLastEditedDate(drawing);
  const localizedAt = drawing.localizedAt
    ? new Date(drawing.localizedAt)
    : undefined;

  if (!editedAt) return localizedAt;
  if (!localizedAt) return editedAt;
  return editedAt > localizedAt ? editedAt : localizedAt;
}

/**
 * Dessins locaux à retirer du stockage hors ligne : inactifs depuis plus de
 * `maxIdleMs` ET ne portant aucun travail non remonté au serveur.
 *
 * Fonction pure (aucun accès IDB) : les `DRLocalDrawingMeta` proviennent du liveQuery
 * déjà maintenu par `fileStore`, et `unsyncedDrawingIds` d'une seule requête indexée
 * (cf. `activityEventRepository.getDrawingIdsWithUnsyncedEvents`). Le tri des
 * conditions va donc du moins cher au plus cher.
 *
 * Chaque garde protège une forme distincte de travail non synchronisé — supprimer la
 * ligne `db.drawings` détruirait aussi l'oplog du dessin (cf.
 * `localDrawingRepository.deleteManyById`), donc aucune ne peut être relâchée :
 *  - id négatif : dessin temporaire jamais monté au serveur (`createTemporaryDrawing`) ;
 *  - format legacy : cf. ci-dessous, l'éviction serait IRRÉVERSIBLE ;
 *  - brouillon présent : modifications locales pas encore poussées ;
 *  - `error` : échec de synchronisation, le dessin porte peut-être du travail à sauver ;
 *  - events d'oplog non poussés ;
 *  - date de référence inconnue : on n'évince jamais ce qu'on ne sait pas dater.
 */
export function selectEvictableLocalDrawings(
  drawings: DRLocalDrawingMeta[],
  unsyncedDrawingIds: Set<number>,
  maxIdleMs: number,
  now: number
): number[] {
  const cutoff = now - maxIdleMs;

  return drawings
    .filter((drawing) => {
      if (drawing.id <= 0) return false;
      if (isLegacyFormatDrawing(drawing)) return false;
      if (drawing.error) return false;
      if (!isDrawingSynced(drawing)) return false;
      if (unsyncedDrawingIds.has(drawing.id)) return false;

      const retentionDate = getLocalRetentionDate(drawing);
      if (!retentionDate) return false;

      return retentionDate.getTime() < cutoff;
    })
    .map((drawing) => drawing.id);
}
